/**
 * Grocery Home Page - Refactored
 * Uses modular architecture with services and components
 */

console.log('[GROCERY HOME] Module loading started...');

import { CategoryService } from '../services/CategoryService.js';
import { ProductService } from '../services/ProductService.js';
import { renderProductCard } from '../components/ProductCard.js';
import { ScrollableRail } from '../components/ScrollableRail.js';
import { SkeletonLoader } from '../components/SkeletonLoader.js';
import { initCurrencyToggle } from '../components/CurrencyToggle.js';
import { escapeHtml } from '../../utils/validation.js';
import currencyManager from '../../utils/currency.js';
import { t } from '../utils/i18n.js';
import { buildMediaUrl } from '../utils/media.js';
import { categoryDetailPath } from '../utils/routes.js';
import type { Category, Product, Store } from '../../types/index.js';

console.log('[GROCERY HOME] All imports loaded successfully!');

/**
 * Render categories grid
 */
function renderCategories(categories: Category[]): void {
    const grid = document.getElementById('categories-grid');
    if (!grid) return;

    if (!categories || categories.length === 0) {
        grid.innerHTML = `
            <div class="col-span-full text-center py-8">
                <p class="text-sm text-gray-500">${escapeHtml(t('No categories available'))}</p>
            </div>
        `;
        return;
    }

    const html = categories.map(category => {
        const name = (category as any).translated_name || category.name;
        const escapedName = escapeHtml(name);
        const escapedSlug = escapeHtml(category.slug || '');
        const logoUrl = (category as any).logo_path ? buildMediaUrl((category as any).logo_path) : null;
        const escapedLogoUrl = logoUrl ? escapeHtml(logoUrl) : '';
        
        const logoHtml = logoUrl 
            ? `<div class="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden"><img src="${escapedLogoUrl}" alt="${escapedName}" class="w-full h-full object-contain" loading="lazy" onerror="this.style.display='none';"></div>`
            : `<div class="w-full h-full rounded-full bg-white flex items-center justify-center group-hover:bg-brand-accent/20 transition-colors duration-300"><i class="fas fa-shopping-basket text-2xl text-brand-primary group-hover:text-brand-accent transition-colors duration-300"></i></div>`;
        
        return `
            <a href="${categoryDetailPath(category.slug || '')}" class="group block w-full">
                <div class="flex flex-col items-center mx-auto" style="max-width: 7rem; padding: 0.25rem; gap: 0.25rem;">
                    <div class="bg-gradient-to-br from-brand-primary to-brand-primary/90 rounded-full flex items-center justify-center shadow-md border-2 border-brand-primary overflow-hidden group-hover:from-brand-accent/80 group-hover:to-brand-accent/70 group-hover:border-brand-accent group-hover:shadow-md group-hover:scale-105 transition-transform duration-200 ease-out" style="width: 4rem; height: 4rem;">
                        ${logoHtml}
                    </div>
                    <p class="text-xs sm:text-sm font-semibold text-slate-800 group-hover:text-brand-primary transition-colors duration-200 text-center w-full px-1 leading-snug">${escapedName}</p>
                </div>
            </a>
        `;
    }).join('');

    grid.innerHTML = html;
}

/**
 * Render discounted products slider
 */
function renderDiscountedProducts(products: Product[], storesLookup: Record<string, Store> = {}): void {
    const productsSection = document.getElementById('discounted-products-section');
    const productsWrapper = document.getElementById('products-slider-wrapper');
    
    if (!productsSection || !productsWrapper) return;
    
    if (!products || products.length === 0) {
        productsSection.style.display = 'none';
        return;
    }
    
    productsSection.style.display = 'block';
    
    // Process products to get individual variants
    const productItems = ProductService.processProductsForDisplay(products, storesLookup).slice(0, 12);
    
    // Render product cards
    const html = productItems.map(item => `
        <div class="discount-product-card flex-none">
            ${renderProductCard(item, storesLookup)}
        </div>
    `).join('');
    productsWrapper.innerHTML = html;
    setupDiscountRailControls();
    
    // Initialize Swiper if available
    const swiperContainer = productsWrapper.closest('.swiper');
    if (typeof (window as any).Swiper !== 'undefined' && swiperContainer && !(swiperContainer as any).swiper) {
        new (window as any).Swiper('.product-slider', {
            slidesPerView: 2,
            spaceBetween: 16,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            breakpoints: {
                640: { slidesPerView: 3, spaceBetween: 16 },
                768: { slidesPerView: 4, spaceBetween: 20 },
                1024: { slidesPerView: 5, spaceBetween: 24 },
            },
        });
    }
}

function setupDiscountRailControls(): void {
    const rail = document.getElementById('products-slider-wrapper');
    const prevButton = document.querySelector('[data-discount-scroll="prev"]');
    const nextButton = document.querySelector('[data-discount-scroll="next"]');

    if (!rail || !(rail instanceof HTMLElement)) return;

    const updateButtons = (): void => {
        const maxScroll = rail.scrollWidth - rail.clientWidth;
        const atStart = rail.scrollLeft <= 4;
        const atEnd = rail.scrollLeft >= maxScroll - 4;

        if (prevButton instanceof HTMLButtonElement) prevButton.disabled = atStart;
        if (nextButton instanceof HTMLButtonElement) nextButton.disabled = atEnd || maxScroll <= 0;
    };

    const scrollByPage = (direction: -1 | 1): void => {
        rail.scrollBy({
            left: direction * Math.max(rail.clientWidth * 0.8, 220),
            behavior: 'smooth',
        });
    };

    if (prevButton instanceof HTMLButtonElement) {
        prevButton.onclick = () => scrollByPage(-1);
    }
    if (nextButton instanceof HTMLButtonElement) {
        nextButton.onclick = () => scrollByPage(1);
    }

    rail.onscroll = updateButtons;
    window.addEventListener('resize', updateButtons);
    requestAnimationFrame(updateButtons);
}

/**
 * Render search results
 */
function renderSearchResults(products: Product[], searchQuery: string): void {
    const searchResultsGrid = document.getElementById('search-results-grid');
    if (!searchResultsGrid) return;
    
    if (!products || products.length === 0) {
        const escapedQuery = escapeHtml(searchQuery);
        const noProductsText = escapeHtml(t('No products found for'));
        searchResultsGrid.innerHTML = `
            <div class="col-span-full text-center py-8">
                <p class="text-sm text-gray-500">${noProductsText} "${escapedQuery}"</p>
            </div>
        `;
        return;
    }
    
    // Process products for display
    const productItems = ProductService.processProductsForDisplay(products);
    
    // Render product cards
    const html = productItems.map(item => renderProductCard(item)).join('');
    searchResultsGrid.innerHTML = html;
}

/**
 * Handle search
 */
async function handleSearch(searchQuery: string): Promise<void> {
    const searchResultsGrid = document.getElementById('search-results-grid');
    if (!searchResultsGrid || !searchQuery) return;
    
    // Show loading state
    const loader = new SkeletonLoader();
    searchResultsGrid.innerHTML = loader.renderProductCardSkeleton(12);
    
    try {
        const products = await ProductService.searchProducts(searchQuery, { limit: 50 });
        renderSearchResults(products, searchQuery);
    } catch (error) {
        console.error('Search failed:', error);
        searchResultsGrid.innerHTML = `
            <div class="col-span-full text-center py-8">
                <p class="text-sm text-red-500">${escapeHtml(t('Error loading search results'))}</p>
            </div>
        `;
    }
}

/**
 * Initialize home page
 */
async function initHomePage(): Promise<void> {
    console.log('[INIT] initHomePage called!');
    
    // Initialize currency toggle if container exists
    const currencyContainer = document.getElementById('currency-toggle-container');
    if (currencyContainer) {
        console.log('[INIT] Initializing currency toggle...');
        initCurrencyToggle('currency-toggle-container', async (newCurrency) => {
            // Reload products when currency changes
            await loadDiscountedProducts();
        });
    } else {
        console.log('[INIT] No currency toggle container found');
    }
    
    // Load categories
    console.log('[INIT] About to load categories...');
    await loadCategories();
    console.log('[INIT] Categories load complete');
    
    // Load discounted products (this will also initialize currency info from API)
    console.log('[INIT] About to load discounted products...');
    await loadDiscountedProducts();
    console.log('[INIT] Discounted products load complete');
    
    // Setup search
    console.log('[INIT] Setting up search...');
    setupSearch();
    
    // Initialize scrollable rails
    console.log('[INIT] Initializing scrollable rails...');
    initializeScrollableRails();
    
    console.log('[INIT] initHomePage completed!');
}

/**
 * Load categories
 */
async function loadCategories(): Promise<void> {
    const grid = document.getElementById('categories-grid');
    console.log('[DEBUG] loadCategories - grid element:', grid);
    if (!grid) return;
    
    const apiUrl = grid.dataset.apiUrl;
    console.log('[DEBUG] loadCategories - apiUrl:', apiUrl);
    if (!apiUrl) {
        console.error('[ERROR] No API URL found in data-api-url attribute');
        return;
    }
    
    try {
        console.log('[DEBUG] Creating CategoryService and fetching categories...');
        const categoryService = new CategoryService();
        const categories = await categoryService.fetchCategories();
        console.log('[DEBUG] Categories fetched:', categories);
        renderCategories(categories);
    } catch (error) {
        console.error('Failed to load categories:', error);
        grid.innerHTML = `
            <div class="col-span-full text-center py-8">
                <p class="text-sm text-red-500">${escapeHtml(t('Error loading categories'))}</p>
            </div>
        `;
    }
}

/**
 * Load discounted products
 */
async function loadDiscountedProducts(): Promise<void> {
    const productsSection = document.getElementById('discounted-products-section');
    const productsWrapper = document.getElementById('products-slider-wrapper');
    
    if (!productsSection || !productsWrapper) return;
    
    // Show loading state
    productsSection.style.display = 'block';
    const loader = new SkeletonLoader();
    productsWrapper.innerHTML = loader.renderProductCardSkeleton(6);
    
    try {
        const currency = currencyManager.getCode();
        const response = await fetch(`/grocery/api/products/discounted/?currency=${currency}&limit=20`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const json = await response.json();
        
        // Apply currency info from API response if available
        if (json.success && json.data?.currency) {
            currencyManager.applyCurrencyInfo(json.data.currency);
        }
        
        const products = json.success && json.data?.products ? json.data.products : [];
        const storesLookup = json.success && json.data?.stores ? json.data.stores : {};
        renderDiscountedProducts(products, storesLookup);
    } catch (error) {
        console.error('Failed to load discounted products:', error);
        productsSection.style.display = 'none';
    }
}

/**
 * Setup search functionality
 */
function setupSearch(): void {
    const searchForm = document.getElementById('grocery-search-form');
    const searchInput = document.getElementById('grocery-search-input');
    
    if (!searchForm || !searchInput || !(searchInput instanceof HTMLInputElement)) return;
    
    searchForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const query = searchInput.value.trim();
        if (query) {
            await handleSearch(query);
        }
    });
    
    // Optional: Search as you type (debounced)
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;
    searchInput.addEventListener('input', (e) => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        const query = (e.target as HTMLInputElement).value.trim();
        if (query.length >= 3) {
            searchTimeout = setTimeout(() => handleSearch(query), 500);
        }
    });
}

/**
 * Initialize scrollable rails
 */
function initializeScrollableRails(): void {
    // Initialize category rail if exists
    const categoryRail = document.querySelector('.category-rail');
    if (categoryRail && categoryRail instanceof HTMLElement) {
        const rail = new ScrollableRail(categoryRail.id || 'category-rail');
        rail.init();
    }
}

console.log('[GROCERY HOME] Setting up initialization...');
console.log('[GROCERY HOME] document.readyState:', document.readyState);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    console.log('[GROCERY HOME] DOM still loading, adding event listener...');
    document.addEventListener('DOMContentLoaded', () => {
        console.log('[GROCERY HOME] DOMContentLoaded fired, initializing...');
        initHomePage();
    });
} else {
    console.log('[GROCERY HOME] DOM already ready, initializing immediately...');
    initHomePage();
}

console.log('[GROCERY HOME] Module fully loaded and initialization setup complete');

// Export for testing
export { initHomePage, renderCategories, renderDiscountedProducts, handleSearch };
